import { useParams } from "react-router-dom";
import { getFooterStrings } from "../i18n/footerStrings";
import { getToolsStrings } from "../i18n/toolsStrings";
import { useLanguages } from "../languages/useLanguages";
import { localizedPath } from "../utils/localizedPath";
import { TOOL_KEYS, toolHref } from "../config/toolRegistry";

export function SiteFooter() {
  const { lang: routeLang = "en" } = useParams();
  const { data: languages } = useLanguages();
  const defaultUrlCode = languages?.find((language) => language.isDefault)?.urlCode ?? "en";
  const t = getFooterStrings(routeLang);
  const toolStrings = getToolsStrings(routeLang);
  const path = (target: string) => localizedPath(routeLang, defaultUrlCode, target);

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div><strong>NetRiskScan</strong><span>{t.tagline}</span></div>
        {/* Pricing and Refund Policy sit in the primary footer nav, on every public page, rather than
            being reachable only from the pricing flow: the terms that govern a paid subscription —
            what it costs, that it renews, and that fees are generally non-refundable — have to be
            findable from anywhere on the site, not just from the page that is trying to sell it. */}
        <nav aria-label="Footer navigation">
          <a href={path("/about.html")}>{t.about}</a>
          <a href={path("/pricing.html")}>{t.pricing}</a>
          <a href={path("/editorial-policy.html")}>{t.editorialPolicy}</a>
          <a href={path("/contact.html")}>{t.contact}</a>
          <a href={path("/privacy.html")}>{t.privacy}</a>
          <a href={path("/terms.html")}>{t.terms}</a>
          <a href={path("/refund-policy.html")}>{t.refundPolicy}</a>
          <a href={path("/disclaimer.html")}>{t.disclaimer}</a>
        </nav>
        {/* All 9 tools, linked directly — the header's own Tools mega menu duplicates these, but a
            footer sitemap is the fallback path for a visitor (or crawler) that never opens it.
            /tools.html itself is retired (edgeRetiredPages.ts -> /); nothing here still
            routes through that directory page. */}
        <nav aria-label="More checks">
          {TOOL_KEYS.map((key) => (
            <a href={toolHref(key, routeLang, defaultUrlCode)} key={key}>{toolStrings.tools[key].title}</a>
          ))}
        </nav>
        {/* Discoverable from every page, not just the home page's Developer Tools section — a
            visitor who lands on a tool or blog page first should still be able to find the CLI
            and SDK repos. */}
        <nav aria-label={t.openSource}>
          <a href="https://github.com/TeamQQ/netriskscan-cli" target="_blank" rel="noopener noreferrer">{t.openSourceCli}</a>
          <a href="https://github.com/TeamQQ/netriskscan-sdk-js" target="_blank" rel="noopener noreferrer">{t.openSourceSdk}</a>
        </nav>
        <small>{t.copyright(new Date().getFullYear())}</small>
      </div>
    </footer>
  );
}
